import {
  FormComboBox,
  FormController,
  FormInput,
  FormSelect,
  FormTextArea,
} from '@/components/form';
import { useAppSelector } from '@/state';
import { ProjectsApi, RfiApi } from '@/state/api';
import { RfiSubmittalsType } from '@/types';
import { rfiLogImpact, rfiLogStatus } from '@/types/enums';
import { toDateInputValue } from '@/utils/dateFields';
import { Box, Checkbox, Flex, Separator } from '@chakra-ui/react';
import { OverlayDialog, toasterApi } from '@ui';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import z from 'zod';

type ManageCardProps = {
  overlayId: string;
  isNew?: boolean;
  defaultRfiData?:
    | RfiSubmittalsType.RfiLog.GetRfiLog['response']['data']
    | null;
};

const ManageCard = ({
  defaultRfiData,
  isNew = false,
  overlayId,
}: ManageCardProps) => {
  const [postRfiLogMutation] = RfiApi.Api.usePostRfiLogMutation();
  const [patchRfiLogMutation] = RfiApi.Api.usePatchRfiLogMutation();

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const { data: getAllProjects } = ProjectsApi.Api.useGetAllProjectsQuery();

  const postSchema = RfiSubmittalsType.RfiLog.post.body;
  const patchSchema = RfiSubmittalsType.RfiLog.patch.body;
  type PostFormType = z.infer<typeof postSchema>;
  type PatchFormType = z.infer<typeof patchSchema>;

  const formSchema = isNew ? postSchema : patchSchema;
  type FormSchemaType = PostFormType | PatchFormType;

  const handleCreateRfi = (formData: FormSchemaType) => {
    const { answered_date, closed_date, ...rest } = formData;

    const payload: any = {
      ...rest,
      export_distribution_list: formData.export_distribution_list || '',
      // Sent unconditionally so a wrong date can be cleared, not only replaced.
      open_date: toDateInputValue(rest.open_date) || null,
      due_date: toDateInputValue(rest.due_date) || null,
      answered_date: toDateInputValue(answered_date) || null,
      closed_date: toDateInputValue(closed_date) || null,
    };

    (isNew
      ? postRfiLogMutation({
          body: payload as PostFormType,
        })
      : patchRfiLogMutation({
          params: {
            rfi_log_id: defaultRfiData?.id as number,
          },
          body: payload,
        })
    )
      .unwrap()
      .then(() => {
        toasterApi({
          type: 'success',
          status: 201,
          code: 'Success',
          message: `RFI ${isNew ? 'created' : 'updated'} successfully`,
        });
      })
      .catch(() => {
        toasterApi({
          type: 'error',
          status: 500,
          code: 'Error',
          message: `Could not ${isNew ? 'create' : 'update'} RFI`,
        });
      })
      .finally(() => {
        OverlayDialog.close(overlayId);
      });
  };

  return (
    <FormController
      key={`rfi-form-${isNew}-${defaultRfiData?.id}`}
      size="lg"
      formSchema={formSchema}
      defaultValues={{
        rfi_number: (isNew ? '' : defaultRfiData?.rfi_number) ?? '',
        subject: (isNew ? '' : defaultRfiData?.subject) ?? '',
        project:
          (isNew ? selectedProject?.id : defaultRfiData?.project) ??
          (selectedProject?.id as number),
        status: (isNew ? 'open' : defaultRfiData?.status) ?? 'open',
        impact: (isNew ? 'none' : defaultRfiData?.impact) ?? 'none',
        discipline: (isNew ? '' : defaultRfiData?.discipline) ?? '',
        spec_section: (isNew ? '' : defaultRfiData?.spec_section) ?? '',
        refs: (isNew ? '' : defaultRfiData?.refs) ?? '',
        question: (isNew ? '' : defaultRfiData?.question) ?? '',
        ball_in_court:
          (isNew ? '' : (defaultRfiData as any)?.ball_in_court) ?? '',
        zone_level: (isNew ? '' : defaultRfiData?.zone_level) ?? '',
        official_response:
          (isNew ? '' : defaultRfiData?.official_response) ?? '',
        remarks: (isNew ? '' : defaultRfiData?.remarks) ?? '',
        open_date: isNew ? '' : toDateInputValue(defaultRfiData?.open_date),
        due_date: isNew ? '' : toDateInputValue(defaultRfiData?.due_date),
        answered_date: isNew
          ? ''
          : toDateInputValue(defaultRfiData?.answered_date),
        closed_date: isNew ? '' : toDateInputValue(defaultRfiData?.closed_date),

        export_to_name: (isNew ? '' : defaultRfiData?.export_to_name) ?? '',
        export_spec_ref: (isNew ? '' : defaultRfiData?.export_spec_ref) ?? '',
        export_contract_drawing_ref:
          (isNew ? '' : defaultRfiData?.export_contract_drawing_ref) ?? '',
        export_shop_drawing_ref:
          (isNew ? '' : defaultRfiData?.export_shop_drawing_ref) ?? '',
        export_distribution_list:
          (isNew ? '' : defaultRfiData?.export_distribution_list) ?? '',
        export_information_not_on_contract:
          (isNew
            ? false
            : defaultRfiData?.export_information_not_on_contract) ?? false,
        export_interpretation_of_contract:
          (isNew ? false : defaultRfiData?.export_interpretation_of_contract) ??
          false,
        export_conflict_in_contract:
          (isNew ? false : defaultRfiData?.export_conflict_in_contract) ??
          false,
        export_coordination_problem:
          (isNew ? false : defaultRfiData?.export_coordination_problem) ??
          false,
      }}
      onFormSubmit={handleCreateRfi}
    >
      {({
        register,
        control,
        errors,
      }: {
        register: UseFormRegister<FormSchemaType>;
        control: Control<FormSchemaType>;
        errors: FieldErrors<FormSchemaType>;
      }) => {
        return (
          <Flex flexDirection="column" gapY={6}>
            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="rfi_number"
              label="RFI Number"
              type="text"
              placeholder="e.g. RFI-001"
              showLabelAtTop
              required
            />

            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="subject"
              label="Subject"
              type="text"
              placeholder="Short title or question"
              showLabelAtTop
              required={isNew ? true : false}
            />

            <Flex gapX={4}>
              <FormComboBox
                register={register}
                control={control}
                errors={errors}
                id="project"
                label="Project"
                keyForDisplay="name"
                keyForValue="id"
                items={getAllProjects ?? []}
                showLabelAtTop
                required={true}
              />
            </Flex>

            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="zone_level"
              label="Zone Level"
              placeholder="e.g. L5 East"
              showLabelAtTop
            />

            <Flex gapX={4}>
              <FormSelect
                register={register}
                control={control}
                errors={errors}
                id="status"
                label="Status"
                keyForDisplay="label"
                keyForValue="value"
                items={rfiLogStatus}
                showLabelAtTop
              />
              <FormSelect
                register={register}
                control={control}
                errors={errors}
                id="impact"
                label="Impact"
                keyForDisplay="label"
                keyForValue="value"
                items={rfiLogImpact}
                showLabelAtTop
              />
            </Flex>

            <Flex gapX={4}>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="open_date"
                label="Open Date"
                showLabelAtTop
                type="date"
                required={isNew ? true : false}
              />
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="due_date"
                label="Due Date"
                showLabelAtTop
                type="date"
                required={isNew ? true : false}
              />
            </Flex>

            <Flex gapX={4}>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="answered_date"
                label="Answered Date"
                showLabelAtTop
                type="date"
              />
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="closed_date"
                label="Closed Date"
                showLabelAtTop
                type="date"
              />
            </Flex>

            <Flex gapX={4}>
              <Box width="200px">
                <FormInput
                  width="200px"
                  register={register}
                  control={control}
                  errors={errors}
                  id="discipline"
                  label="Discipline"
                  showLabelAtTop
                />
              </Box>
              <Box width="200px">
                <FormInput
                  width="200px"
                  register={register}
                  control={control}
                  errors={errors}
                  id="ball_in_court"
                  label="Ball in Court"
                  placeholder="e.g. Procore / GC / John Smith"
                  showLabelAtTop
                />
              </Box>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="spec_section"
                label="Spec Section"
                showLabelAtTop
              />
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="refs"
                label="Refs"
                showLabelAtTop
              />
            </Flex>

            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="remarks"
              label="Remarks"
              showLabelAtTop
            />

            <Separator />

            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="export_to_name"
              label="Default Export To"
              showLabelAtTop
            />

            <FormInput
              register={register}
              control={control}
              errors={errors}
              id="export_spec_ref"
              label="Default Export Spec Ref"
              showLabelAtTop
            />

            <Flex gapX={4}>
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="export_contract_drawing_ref"
                label="Default Contract Drawing Ref"
                showLabelAtTop
              />
              <FormInput
                register={register}
                control={control}
                errors={errors}
                id="export_shop_drawing_ref"
                label="Default Shop Drawing Ref"
                showLabelAtTop
              />
            </Flex>

            <FormTextArea
              label="Default Export Distribution List"
              id="export_distribution_list"
              register={register}
              control={control}
              errors={errors}
              showLabelAtTop
              minH="5lh"
              maxH="5lh"
            />

            <Controller
              name="export_information_not_on_contract"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox.Root
                  checked={!!field.value}
                  onCheckedChange={(e: any) => {
                    const next = typeof e === 'boolean' ? e : e?.checked;
                    field.onChange(next === true);
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>
                    Default: Information not on contract
                  </Checkbox.Label>
                </Checkbox.Root>
              )}
            />

            <Controller
              name="export_interpretation_of_contract"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox.Root
                  checked={!!field.value}
                  onCheckedChange={(e: any) => {
                    const next = typeof e === 'boolean' ? e : e?.checked;
                    field.onChange(next === true);
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>
                    Default: Interpretation of contract
                  </Checkbox.Label>
                </Checkbox.Root>
              )}
            />

            <Controller
              name="export_conflict_in_contract"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox.Root
                  checked={!!field.value}
                  onCheckedChange={(e: any) => {
                    const next = typeof e === 'boolean' ? e : e?.checked;
                    field.onChange(next === true);
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>Default: Conflict in contract</Checkbox.Label>
                </Checkbox.Root>
              )}
            />

            <Controller
              name="export_coordination_problem"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox.Root
                  checked={!!field.value}
                  onCheckedChange={(e: any) => {
                    const next = typeof e === 'boolean' ? e : e?.checked;
                    field.onChange(next === true);
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <Checkbox.Label>Default: Coordination problem</Checkbox.Label>
                </Checkbox.Root>
              )}
            />
          </Flex>
        );
      }}
    </FormController>
  );
};

export default ManageCard;
